import { Plugin, PluginKey } from "prosemirror-state";
import type { Transaction } from "prosemirror-state";

export const sidSyncKey = new PluginKey("sid-sync");

const LETTERS = "abcdefghijklmnopqrstuvwxyz";

/**
 * Given a 0-based index and a depth (1-based), produce the SID segment.
 * Odd depths use numbers (1-based), even depths use letters.
 */
function indexToSegment(index: number, depth: number): string {
  if (depth % 2 === 1) {
    return String(index + 1);
  }
  return LETTERS[index] ?? String.fromCharCode(97 + index);
}

/**
 * Compute correct SIDs from a flat list of { level } values.
 *
 * We walk the flat list and maintain a stack of (parentSID, depth).
 * Each node's SID = parentSID + segment(indexAmongSiblings, depth).
 */
export function computeSIDs(levels: number[]): string[] {
  const sids: string[] = [];
  // Stack: { sid, level, childCount } — childCount tracks how many children
  // we've seen at the next level
  const stack: Array<{ sid: string; level: number; childCount: number }> = [
    { sid: "", level: 0, childCount: 0 },
  ];

  for (const level of levels) {
    // Pop stack until the top is the parent (level < current level)
    while (stack.length > 1 && stack[stack.length - 1]!.level >= level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1]!;
    const segment = indexToSegment(parent.childCount, level);
    const sid = parent.sid ? parent.sid + segment : segment;

    parent.childCount++;
    sids.push(sid);
    stack.push({ sid, level, childCount: 0 });
  }

  return sids;
}

/**
 * Plugin that recalculates SID attrs on every doc-changing transaction.
 * This ensures SIDs stay correct after Enter (split), delete, indent, etc.
 */
export function sidSyncPlugin(): Plugin {
  return new Plugin({
    key: sidSyncKey,

    appendTransaction(transactions, _oldState, newState) {
      // Only run if the document actually changed
      const docChanged = transactions.some((tr) => tr.docChanged);
      if (!docChanged) return null;

      // Collect current levels and current SIDs
      const levels: number[] = [];
      const currentSIDs: string[] = [];
      const positions: number[] = [];

      newState.doc.forEach((node, offset) => {
        if (node.type.name === "statement") {
          levels.push(node.attrs.level as number);
          currentSIDs.push(node.attrs.sid as string);
          positions.push(offset);
        }
      });

      // Compute correct SIDs
      const correctSIDs = computeSIDs(levels);

      // Check if any SID needs updating
      let needsUpdate = false;
      for (let i = 0; i < correctSIDs.length; i++) {
        if (correctSIDs[i] !== currentSIDs[i]) {
          needsUpdate = true;
          break;
        }
      }

      if (!needsUpdate) return null;

      // Build a transaction that updates all mismatched SIDs
      let tr: Transaction = newState.tr;
      for (let i = 0; i < correctSIDs.length; i++) {
        if (correctSIDs[i] !== currentSIDs[i]) {
          tr = tr.setNodeMarkup(positions[i]!, undefined, {
            ...newState.doc.child(i).attrs,
            sid: correctSIDs[i],
          });
        }
      }

      return tr;
    },
  });
}
